import { lazy, Suspense, useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import {
    Alert,
    Badge,
    Box,
    Button,
    Flex,
    Grid,
    Heading,
    Input,
    Link,
    NativeSelect,
    Spinner,
    Stack,
    Switch,
    Text,
    Textarea,
} from '@chakra-ui/react';
import { FiCheck, FiExternalLink, FiRefreshCw, FiSearch, FiX } from 'react-icons/fi';
import { useColorModeValue } from '../components/ui/color-mode';
import {
    getCustomMetricSuggestions,
    getCustomMetricPreviewJobs,
    getCustomMetrics,
    previewCustomMetricSuggestion,
    reviewCustomMetricSuggestion,
} from '../api';
import useCustomMetricScan from '../hooks/useCustomMetricScan';
import { useAuth0 } from '@auth0/auth0-react';
import { getLoginRedirectOptions, isRecoverableAuthError } from '../utils/auth0Errors';

const MetricPreviewRechartsChart = lazy(() =>
    import('recharts').then(({
        CartesianGrid,
        Line,
        LineChart,
        ResponsiveContainer,
        Tooltip,
        XAxis,
        YAxis,
    }) => ({
        default: function MetricPreviewRechartsChart({
            chartData,
            gridStroke,
            tooltipBg,
            tooltipBorder,
            unit,
        }) {
            return (
                <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={chartData} margin={{ top: 8, right: 24, left: 8, bottom: 8 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} />
                        <XAxis dataKey="date" tick={{ fontSize: 12 }} minTickGap={20} />
                        <YAxis
                            tick={{ fontSize: 12 }}
                            width={90}
                            tickFormatter={(value) => formatValue(value, unit)}
                        />
                        <Tooltip
                            content={({ active, payload, label }) => {
                                if (!active || !payload?.length) return null;
                                const point = payload[0]?.payload || {};
                                return (
                                    <Stack
                                        gap={1}
                                        bg={tooltipBg}
                                        borderWidth="1px"
                                        borderColor={tooltipBorder}
                                        borderRadius="md"
                                        p={3}
                                    >
                                        <Text fontWeight="semibold">{formatDate(label)}</Text>
                                        <Text fontSize="sm">{formatValue(payload[0].value, unit)}</Text>
                                        {point.filingDate && (
                                            <Text fontSize="xs" color="gray.500">
                                                Filed {formatDate(point.filingDate)}
                                            </Text>
                                        )}
                                    </Stack>
                                );
                            }}
                        />
                        <Line type="monotone" dataKey="value" stroke="#2C7A7B" strokeWidth={2} dot />
                    </LineChart>
                </ResponsiveContainer>
            );
        },
    })),
);

const initialState = {
    suggestions: [],
    suggestionsTicker: '',
    status: 'idle',
    reviewStatusByKey: {},
    previewStatusByKey: {},
    error: null,
};

const DEFAULT_TICKER = 'HOOD';
const VALUE_FORMATTER = new Intl.NumberFormat('en-US', { maximumFractionDigits: 4 });
const PREVIEW_DEFINITION_FIELDS = new Set(['display_name', 'metric_key', 'unit', 'row_label', 'description']);

function previewDefinition(draft, suggestion) {
    return {
        display_name: draft.display_name || '', metric_key: draft.metric_key || '',
        unit: draft.unit || '', row_label: draft.row_label || '', description: draft.description || '',
        example_points: suggestion.example_points || {},
    };
}

function canonical(value) {
    if (Array.isArray(value)) return value.map(canonical);
    if (value && typeof value === 'object') {
        return Object.fromEntries(Object.keys(value).sort().map((key) => [key, canonical(value[key])]));
    }
    return value;
}

function matchingPreview(job, definition) {
    if (!job || JSON.stringify(canonical(job.definition)) !== JSON.stringify(canonical(definition))) return null;
    return {
        ...(job.result || {}),
        history_status: job.status === 'complete' ? 'complete' : job.status === 'failed' ? 'error' : 'loading',
        progress: job.status === 'queued' ? (job.error || 'History extraction queued…')
            : job.total_filings == null ? 'Finding SEC filings…'
                : `Processed ${job.completed_filings} of ${job.total_filings} filings.`,
        warnings: job.status === 'failed' ? [job.error || 'History extraction failed. Please try again.']
            : job.result?.warnings || [],
    };
}

function reducer(state, action) {
    switch (action.type) {
        case 'load-start':
            return {
                ...state,
                suggestions: [],
                suggestionsTicker: action.ticker,
                status: 'loading',
                reviewStatusByKey: {},
                previewStatusByKey: {},
                error: null,
            };
        case 'load-success':
            if (action.ticker !== state.suggestionsTicker) {
                return state;
            }
            return {
                ...state,
                status: 'success',
                suggestions: action.suggestions,
                error: null,
            };
        case 'load-error':
            if (action.ticker !== state.suggestionsTicker) {
                return state;
            }
            return { ...state, status: 'error', error: action.error };
        case 'clear-suggestions':
            return {
                ...state,
                suggestions: [],
                suggestionsTicker: '',
                status: 'idle',
                reviewStatusByKey: {},
                previewStatusByKey: {},
                error: null,
            };
        case 'review-start':
            return {
                ...state,
                reviewStatusByKey: {
                    ...state.reviewStatusByKey,
                    [action.key]: 'loading',
                },
                error: null,
            };
        case 'review-success':
            return {
                ...state,
                suggestions: state.suggestions.filter(
                    (suggestion) => suggestion.suggestion_key !== action.key,
                ),
                reviewStatusByKey: {
                    ...state.reviewStatusByKey,
                    [action.key]: 'success',
                },
                error: null,
            };
        case 'review-error':
            return {
                ...state,
                reviewStatusByKey: {
                    ...state.reviewStatusByKey,
                    [action.key]: 'error',
                },
                error: action.error,
            };
        case 'preview-start':
            return {
                ...state,
                previewStatusByKey: {
                    ...state.previewStatusByKey,
                    [action.key]: 'loading',
                },
                error: null,
            };
        case 'preview-success':
            return {
                ...state,
                previewStatusByKey: {
                    ...state.previewStatusByKey,
                    [action.key]: 'success',
                },
                error: null,
            };
        case 'preview-error':
            return {
                ...state,
                previewStatusByKey: {
                    ...state.previewStatusByKey,
                    [action.key]: 'error',
                },
                error: action.error,
            };
        case 'clear-preview': {
            const nextStatuses = { ...state.previewStatusByKey };
            delete nextStatuses[action.key];
            return {
                ...state,
                previewStatusByKey: nextStatuses,
            };
        }
        default:
            return state;
    }
}

function normalizeTicker(value) {
    return String(value || '').trim().toUpperCase();
}

function formatDate(value) {
    if (!value) return 'Unknown date';
    const parsed = new Date(`${value}T00:00:00Z`);
    if (Number.isNaN(parsed.getTime())) return value;
    return parsed.toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        timeZone: 'UTC',
    });
}

function formatValue(value, unit) {
    if (value === null || value === undefined || value === '') return 'n/a';
    const number = Number(value);
    const formatted = Number.isNaN(number)
        ? String(value)
        : VALUE_FORMATTER.format(number);
    return unit ? `${formatted} ${unit}` : formatted;
}

function buildInitialDrafts(suggestions) {
    return suggestions.reduce((acc, suggestion) => {
        acc[suggestion.suggestion_key] = {
            display_name: suggestion.display_name || '',
            metric_key: suggestion.metric_key || '',
            unit: suggestion.unit || '',
            row_label: suggestion.row_label || suggestion.display_name || '',
            description: suggestion.description || '',
            is_public: true,
            public_noindex: false,
            public_slug: '',
            seo_title: '',
            seo_description: '',
            rejection_reason: '',
        };
        return acc;
    }, {});
}

function MetricPreviewChart({ values, unit }) {
    const gridStroke = useColorModeValue('#E2E8F0', '#2D3748');
    const tooltipBg = useColorModeValue('white', 'gray.800');
    const tooltipBorder = useColorModeValue('gray.200', 'gray.700');
    const chartData = useMemo(
        () => values.map((value) => ({
            date: value.period_end,
            value: Number(value.value),
            filingDate: value.filing_date,
        })),
        [values],
    );

    if (chartData.length < 2) {
        return null;
    }

    return (
        <Box h="260px" borderWidth="1px" borderColor={tooltipBorder} borderRadius="md" p={3}>
            <Suspense fallback={<Flex h="100%" align="center" justify="center"><Spinner size="sm" /></Flex>}>
                <MetricPreviewRechartsChart
                    chartData={chartData}
                    gridStroke={gridStroke}
                    tooltipBg={tooltipBg}
                    tooltipBorder={tooltipBorder}
                    unit={unit}
                />
            </Suspense>
        </Box>
    );
}

function formatConflictAlternate(alternate, unit) {
    const form = alternate.form || 'filing';
    const method = (alternate.extraction_method || 'extraction').replaceAll('_', ' ');
    return `${formatValue(alternate.value, unit)} from ${form} (${method})`;
}

function MetricPreviewTable({ values, unit }) {
    const borderColor = useColorModeValue('gray.200', 'gray.700');
    const headerBg = useColorModeValue('gray.50', 'gray.800');
    const muted = useColorModeValue('gray.600', 'gray.300');
    const conflictBg = useColorModeValue('orange.50', 'orange.950');

    if (!values.length) {
        return (
            <Text color={muted} fontSize="sm">
                No historical values were found for this definition.
            </Text>
        );
    }

    return (
        <Box borderWidth="1px" borderColor={borderColor} borderRadius="md" overflowX="auto">
            <Box as="table" w="100%" fontSize="sm">
                <Box as="thead" bg={headerBg}>
                    <Box as="tr">
                        <Box as="th" textAlign="left" p={2}>Period</Box>
                        <Box as="th" textAlign="right" p={2}>Value</Box>
                        <Box as="th" textAlign="left" p={2}>Form</Box>
                        <Box as="th" textAlign="left" p={2}>Filed</Box>
                        <Box as="th" textAlign="left" p={2}>Method</Box>
                        <Box as="th" textAlign="right" p={2}>Confidence</Box>
                        <Box as="th" textAlign="left" p={2}>Source</Box>
                    </Box>
                </Box>
                <Box as="tbody">
                    {values.map((value, index) => {
                        const conflict = value.period_conflict;
                        const alternates = conflict?.alternates || [];
                        return (
                            <Box
                                as="tr"
                                key={`${value.period_end}-${value.accession_number || index}`}
                                borderTopWidth="1px"
                                borderColor={borderColor}
                                bg={conflict ? conflictBg : undefined}
                            >
                                <Box as="td" p={2}>
                                    <Stack gap={1}>
                                        <Text>{formatDate(value.period_end)}</Text>
                                        {conflict && (
                                            <Badge colorPalette="orange" variant="subtle" w="fit-content">
                                                Tie broken
                                            </Badge>
                                        )}
                                    </Stack>
                                </Box>
                                <Box as="td" p={2} textAlign="right">
                                    <Stack gap={1} align="flex-end">
                                        <Text>{formatValue(value.value, unit)}</Text>
                                        {alternates.length > 0 && (
                                            <Text color={muted} fontSize="xs">
                                                Also found {alternates.map((item) => formatConflictAlternate(item, unit)).join('; ')}
                                            </Text>
                                        )}
                                    </Stack>
                                </Box>
                                <Box as="td" p={2}>{value.form || 'n/a'}</Box>
                                <Box as="td" p={2}>{formatDate(value.filing_date)}</Box>
                                <Box as="td" p={2}>{value.extraction_method || 'n/a'}</Box>
                                <Box as="td" p={2} textAlign="right">
                                    {Math.round(Number(value.confidence || 0) * 100)}%
                                </Box>
                                <Box as="td" p={2}>
                                    {value.source_url ? (
                                        <Link href={value.source_url} target="_blank" rel="noopener noreferrer" color="teal.600">
                                            Filing
                                        </Link>
                                    ) : (
                                        <Text as="span" color={muted}>n/a</Text>
                                    )}
                                </Box>
                            </Box>
                        );
                    })}
                </Box>
            </Box>
        </Box>
    );
}

function MetricSuggestionCard({
    suggestion,
    draft,
    onDraftChange,
    onPreview,
    onApprove,
    onReject,
    reviewStatus,
    previewStatus,
    preview,
}) {
    const borderColor = useColorModeValue('gray.200', 'gray.700');
    const cardBg = useColorModeValue('white', 'gray.900');
    const muted = useColorModeValue('gray.600', 'gray.300');
    const sampleValues = suggestion.sample_values || [];
    const isReviewing = reviewStatus === 'loading';
    const isPreviewing = previewStatus === 'loading';
    const previewValues = preview?.values || [];
    const canApprove = Boolean(preview) && (
        preview.history_status ? preview.history_status === 'complete' : true
    );

    return (
        <Box borderWidth="1px" borderColor={borderColor} borderRadius="md" bg={cardBg} p={4}>
            <Stack gap={4}>
                <Flex justify="space-between" align="flex-start" gap={3} wrap="wrap">
                    <Box>
                        <Flex align="center" gap={2} wrap="wrap">
                            <Heading size="sm">{suggestion.display_name}</Heading>
                            <Badge colorPalette={suggestion.detection_method === 'table_scan' ? 'green' : 'blue'}>
                                {suggestion.detection_method}
                            </Badge>
                            <Badge variant="outline">
                                {Math.round(Number(suggestion.confidence || 0) * 100)}% confidence
                            </Badge>
                        </Flex>
                        <Text color={muted} fontSize="sm" mt={1}>
                            {suggestion.ticker} / {suggestion.metric_key}
                        </Text>
                    </Box>
                    {suggestion.source_url && (
                        <Button
                            as={Link}
                            href={suggestion.source_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            size="sm"
                            variant="outline"
                        >
                            <FiExternalLink />
                            Source filing
                        </Button>
                    )}
                </Flex>

                <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={3}>
                    <Box>
                        <Text fontSize="sm" fontWeight="semibold" mb={1}>Display name</Text>
                        <Input
                            value={draft.display_name}
                            onChange={(event) => onDraftChange('display_name', event.target.value)}
                        />
                    </Box>
                    <Box>
                        <Text fontSize="sm" fontWeight="semibold" mb={1}>Metric key</Text>
                        <Input
                            value={draft.metric_key}
                            onChange={(event) => onDraftChange('metric_key', event.target.value)}
                        />
                    </Box>
                    <Box>
                        <Text fontSize="sm" fontWeight="semibold" mb={1}>Unit</Text>
                        <Input
                            value={draft.unit}
                            onChange={(event) => onDraftChange('unit', event.target.value)}
                        />
                    </Box>
                    <Box>
                        <Text fontSize="sm" fontWeight="semibold" mb={1}>Row label</Text>
                        <Input
                            value={draft.row_label}
                            onChange={(event) => onDraftChange('row_label', event.target.value)}
                        />
                    </Box>
                </Grid>

                <Box>
                    <Text fontSize="sm" fontWeight="semibold" mb={1}>Description</Text>
                    <Textarea
                        value={draft.description}
                        onChange={(event) => onDraftChange('description', event.target.value)}
                        rows={2}
                    />
                </Box>

                <Box borderWidth="1px" borderColor={borderColor} borderRadius="md" p={3}>
                    <Stack gap={3}>
                        <Flex justify="space-between" align="center" gap={3} wrap="wrap">
                            <Box>
                                <Text fontSize="sm" fontWeight="semibold">Public page publishing</Text>
                                <Text color={muted} fontSize="sm">
                                    Approved public metrics can appear in public APIs, SEO pages, and the sitemap.
                                </Text>
                            </Box>
                            <Switch.Root
                                size="sm"
                                checked={draft.is_public !== false}
                                onCheckedChange={(event) => onDraftChange('is_public', event.checked)}
                            >
                                <Switch.HiddenInput aria-label="Publish metric publicly" />
                                <Switch.Control>
                                    <Switch.Thumb />
                                </Switch.Control>
                                <Switch.Label fontSize="sm">Public</Switch.Label>
                            </Switch.Root>
                        </Flex>

                        <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={3}>
                            <Flex justify="space-between" align="center" gap={3} borderWidth="1px" borderColor={borderColor} borderRadius="md" p={3}>
                                <Box>
                                    <Text fontSize="sm" fontWeight="semibold">Noindex</Text>
                                    <Text color={muted} fontSize="sm">
                                        Keep the direct page available but omit it from SEO indexes and sitemap entries.
                                    </Text>
                                </Box>
                                <Switch.Root
                                    size="sm"
                                    checked={draft.public_noindex === true}
                                    onCheckedChange={(event) => onDraftChange('public_noindex', event.checked)}
                                    disabled={draft.is_public === false}
                                >
                                    <Switch.HiddenInput aria-label="Mark public metric noindex" />
                                    <Switch.Control>
                                        <Switch.Thumb />
                                    </Switch.Control>
                                </Switch.Root>
                            </Flex>
                            <Box>
                                <Text fontSize="sm" fontWeight="semibold" mb={1}>Public slug</Text>
                                <Input
                                    value={draft.public_slug}
                                    onChange={(event) => onDraftChange('public_slug', event.target.value)}
                                    placeholder="Optional; defaults from metric key"
                                />
                            </Box>
                            <Box>
                                <Text fontSize="sm" fontWeight="semibold" mb={1}>SEO title</Text>
                                <Input
                                    value={draft.seo_title}
                                    onChange={(event) => onDraftChange('seo_title', event.target.value)}
                                    placeholder="Optional"
                                />
                            </Box>
                        </Grid>

                        <Box>
                            <Text fontSize="sm" fontWeight="semibold" mb={1}>SEO description</Text>
                            <Textarea
                                value={draft.seo_description}
                                onChange={(event) => onDraftChange('seo_description', event.target.value)}
                                placeholder="Optional"
                                rows={2}
                            />
                        </Box>
                    </Stack>
                </Box>

                <Box>
                    <Text fontSize="sm" fontWeight="semibold" mb={2}>Sample values</Text>
                    {sampleValues.length ? (
                        <Flex gap={2} wrap="wrap">
                            {sampleValues.slice(0, 6).map((sample) => (
                                <Badge key={`${sample.period_end}-${sample.value}`} variant="subtle" colorPalette="gray">
                                    {formatDate(sample.period_end)}: {formatValue(sample.value, suggestion.unit)}
                                </Badge>
                            ))}
                        </Flex>
                    ) : (
                        <Text color={muted} fontSize="sm">No sample values captured.</Text>
                    )}
                </Box>

                {preview && (
                    <Stack gap={3}>
                        <Stack gap={1}>
                            <Text fontWeight="semibold">Historical extraction preview</Text>
                            {preview.history_status === 'loading' ? (
                                <Text color={muted} fontSize="sm">
                                    {preview.progress} You can leave this page and check back later.
                                </Text>
                            ) : (
                                <Text color={muted} fontSize="sm">
                                    {preview.value_count || 0} unique period{preview.value_count === 1 ? '' : 's'} found.
                                    {preview.conflict_count
                                        ? ` ${preview.conflict_count} same-period tie${preview.conflict_count === 1 ? '' : 's'} auto-resolved.`
                                        : ''}
                                    {' '}Review these before adding them to the database.
                                </Text>
                            )}
                        </Stack>
                        {(preview.warnings || []).map((warning) => (
                            <Alert.Root key={warning} status="warning" borderRadius="md">
                                <Alert.Indicator />
                                <Alert.Title>{warning}</Alert.Title>
                            </Alert.Root>
                        ))}
                        {preview.history_status !== 'loading' && (
                            <MetricPreviewChart values={previewValues} unit={draft.unit || suggestion.unit} />
                        )}
                        {preview.history_status !== 'loading' && (
                            <MetricPreviewTable values={previewValues} unit={draft.unit || suggestion.unit} />
                        )}
                    </Stack>
                )}

                <Grid templateColumns={{ base: '1fr', md: '1fr auto auto auto' }} gap={3} alignItems="end">
                    <Box>
                        <Text fontSize="sm" fontWeight="semibold" mb={1}>Rejection reason</Text>
                        <Input
                            value={draft.rejection_reason}
                            onChange={(event) => onDraftChange('rejection_reason', event.target.value)}
                            placeholder="Optional"
                        />
                    </Box>
                    <Button
                        colorPalette="blue"
                        variant="outline"
                        onClick={onPreview}
                        loading={isPreviewing}
                        disabled={isPreviewing || isReviewing}
                    >
                        <FiSearch />
                        Find history
                    </Button>
                    <Button
                        colorPalette="green"
                        onClick={onApprove}
                        loading={isReviewing}
                        disabled={isReviewing || isPreviewing || !canApprove}
                    >
                        <FiCheck />
                        Add to database
                    </Button>
                    <Button
                        colorPalette="red"
                        variant="outline"
                        onClick={onReject}
                        loading={isReviewing}
                        disabled={isReviewing}
                    >
                        <FiX />
                        Reject
                    </Button>
                </Grid>
            </Stack>
        </Box>
    );
}

export default function CustomMetricsAdmin() {
    const { getAccessTokenSilently, loginWithRedirect, user } = useAuth0();
    const [tickerInput, setTickerInput] = useState(() => {
        try { return sessionStorage.getItem('custom-metrics-ticker') || DEFAULT_TICKER; }
        catch { return DEFAULT_TICKER; }
    });
    const [limit, setLimit] = useState(4);
    const [metricsRevision, setMetricsRevision] = useState(0);
    const [existingMetrics, setExistingMetrics] = useState({ ticker: '', status: 'idle', metrics: [] });
    const [state, dispatch] = useReducer(reducer, initialState);
    const [drafts, setDrafts] = useState({});
    const previewRequestIds = useRef({});
    const [previewJobs, setPreviewJobs] = useState({ ticker: '', jobs: [] });
    const [previewPollingError, setPreviewPollingError] = useState(null);
    const loadRequestId = useRef(0);
    const pageBg = useColorModeValue('gray.50', 'gray.950');
    const panelBg = useColorModeValue('white', 'gray.900');
    const borderColor = useColorModeValue('gray.200', 'gray.700');
    const muted = useColorModeValue('gray.600', 'gray.300');

    const ticker = normalizeTicker(tickerInput);
    const loading = state.status === 'loading';
    useEffect(() => {
        try { sessionStorage.setItem('custom-metrics-ticker', tickerInput); }
        catch { /* Storage may be unavailable; jobs still persist on the server. */ }
    }, [tickerInput]);

    const getAdminToken = useCallback(async () => {
        try {
            return await getAccessTokenSilently({ detailedResponse: false });
        } catch (err) {
            if (isRecoverableAuthError(err)) {
                await loginWithRedirect(getLoginRedirectOptions());
                return null;
            }
            throw err;
        }
    }, [getAccessTokenSilently, loginWithRedirect]);

    const loadSuggestionsForTicker = useCallback(async (targetTicker) => {
        if (!targetTicker) {
            loadRequestId.current += 1;
            setDrafts({});
            dispatch({ type: 'clear-suggestions' });
            return;
        }

        const requestId = loadRequestId.current + 1;
        loadRequestId.current = requestId;
        dispatch({ type: 'load-start', ticker: targetTicker });
        try {
            const token = await getAdminToken();
            if (!token) return;
            const [payload, history] = await Promise.all([
                getCustomMetricSuggestions(targetTicker, token),
                getCustomMetricPreviewJobs(targetTicker, token),
            ]);
            if (loadRequestId.current !== requestId) return;
            const suggestions = payload?.suggestions || [];
            dispatch({ type: 'load-success', ticker: targetTicker, suggestions });
            const jobs = history?.jobs || [];
            setPreviewJobs({ ticker: targetTicker, jobs });
            const initialDrafts = buildInitialDrafts(suggestions);
            for (const job of jobs) {
                if (initialDrafts[job.suggestion_key]) {
                    // Restore the extraction inputs alongside the durable result.
                    for (const field of PREVIEW_DEFINITION_FIELDS) {
                        if (job.definition[field] !== undefined) initialDrafts[job.suggestion_key][field] = job.definition[field];
                    }
                }
            }
            setDrafts(initialDrafts);
        } catch (err) {
            if (loadRequestId.current !== requestId) return;
            dispatch({
                type: 'load-error',
                ticker: targetTicker,
                error: err?.message || 'Unable to load custom metric suggestions.',
            });
        }
    }, [getAdminToken]);

    const loadSuggestions = useCallback(() => {
        setMetricsRevision((revision) => revision + 1);
        return loadSuggestionsForTicker(ticker);
    }, [loadSuggestionsForTicker, ticker]);

    useEffect(() => {
        if (!ticker) return;
        const controller = new AbortController();
        setExistingMetrics({ ticker, status: 'loading', metrics: [] });
        getCustomMetrics(ticker, { signal: controller.signal }).then((payload) => {
            if (controller.signal.aborted) return;
            setExistingMetrics({ ticker, status: 'success', metrics: payload?.metrics || [] });
        }).catch(() => {
            if (controller.signal.aborted) return;
            setExistingMetrics({ ticker, status: 'error', metrics: [] });
        });
        return () => controller.abort();
    }, [ticker, metricsRevision]);

    useEffect(() => {
        loadSuggestionsForTicker(ticker);
    }, [loadSuggestionsForTicker, ticker]);

    const handleDraftChange = (suggestionKey, field, value) => {
        setDrafts((current) => ({
            ...current,
            [suggestionKey]: {
                ...(current[suggestionKey] || {}),
                [field]: value,
            },
        }));
        if (PREVIEW_DEFINITION_FIELDS.has(field)) {
            previewRequestIds.current[suggestionKey] = (previewRequestIds.current[suggestionKey] || 0) + 1;
            dispatch({ type: 'clear-preview', key: suggestionKey });
        }
    };

    const handleScanComplete = useCallback((completedTicker) => {
        setMetricsRevision((revision) => revision + 1);
        void loadSuggestionsForTicker(completedTicker);
    }, [loadSuggestionsForTicker]);
    const { job: scanJob, scanning, error: scanError, startScan } = useCustomMetricScan({
        ticker, getToken: getAdminToken, onComplete: handleScanComplete,
    });

    useEffect(() => {
        setPreviewPollingError(null);
        if (previewJobs.ticker !== ticker || !previewJobs.jobs.some((job) => ['queued', 'running'].includes(job.status))) return;
        let cancelled = false;
        let timer;
        const poll = async () => {
            try {
                const token = await getAdminToken();
                if (!token || cancelled) return;
                const payload = await getCustomMetricPreviewJobs(ticker, token);
                if (cancelled) return;
                setPreviewJobs({ ticker, jobs: payload?.jobs || [] });
            } catch {
                if (cancelled) return;
                setPreviewPollingError('Unable to check history progress. Reconnecting…');
                timer = window.setTimeout(poll, 5000);
            }
        };
        timer = window.setTimeout(poll, 2500);
        return () => { cancelled = true; window.clearTimeout(timer); };
    }, [ticker, previewJobs, getAdminToken]);

    const getPreview = (suggestion) => matchingPreview(
        previewJobs.ticker === ticker ? previewJobs.jobs.find((job) => job.suggestion_key === suggestion.suggestion_key) : null,
        previewDefinition(drafts[suggestion.suggestion_key] || {}, suggestion),
    );

    const handlePreview = async (suggestion) => {
        const key = suggestion.suggestion_key;
        const requestId = loadRequestId.current;
        const previewRequestId = (previewRequestIds.current[key] || 0) + 1;
        previewRequestIds.current[key] = previewRequestId;
        const definition = previewDefinition(drafts[key] || {}, suggestion);
        dispatch({ type: 'preview-start', key });
        try {
            const token = await getAdminToken();
            if (!token || loadRequestId.current !== requestId || previewRequestIds.current[key] !== previewRequestId) return;
            const { job } = await previewCustomMetricSuggestion(ticker, key, { definition }, token);
            if (loadRequestId.current !== requestId) return;
            setPreviewJobs((current) => ({ ticker,
                jobs: [...(current.ticker === ticker ? current.jobs.filter((item) => item.suggestion_key !== key) : []), job],
            }));
            if (previewRequestIds.current[key] === previewRequestId) {
                dispatch({ type: 'preview-success', key, preview: null });
            }
        } catch (err) {
            if (loadRequestId.current !== requestId || previewRequestIds.current[key] !== previewRequestId) return;
            dispatch({ type: 'preview-error', key, error: err?.message || 'Unable to queue historical extraction.' });
        }
    };

    const handleReview = async (suggestion, action) => {
        const draft = drafts[suggestion.suggestion_key] || {};
        const preview = getPreview(suggestion);
        dispatch({ type: 'review-start', key: suggestion.suggestion_key });
        try {
            const token = await getAdminToken();
            if (!token) return;
            await reviewCustomMetricSuggestion(ticker, suggestion.suggestion_key, {
                action,
                reviewedBy: user?.email || 'dcf-teacher-admin',
                rejectionReason: draft.rejection_reason,
                definition: action === 'approve'
                    ? {
                        display_name: draft.display_name,
                        metric_key: draft.metric_key,
                        unit: draft.unit,
                        row_label: draft.row_label,
                        description: draft.description,
                        example_points: suggestion.example_points || {},
                        is_public: draft.is_public !== false,
                        public_noindex: draft.public_noindex === true,
                        public_slug: draft.public_slug,
                        seo_title: draft.seo_title,
                        seo_description: draft.seo_description,
                    }
                    : {},
                previewValues: action === 'approve' ? preview?.values || [] : [],
            }, token);
            dispatch({ type: 'review-success', key: suggestion.suggestion_key });
            if (action === 'approve') setMetricsRevision((revision) => revision + 1);
        } catch (err) {
            dispatch({
                type: 'review-error',
                key: suggestion.suggestion_key,
                error: err?.message || `Unable to ${action} suggestion.`,
            });
        }
    };

    return (
        <Box bg={pageBg} minH="calc(100vh - var(--nav-h))" py={8} px={{ base: 4, md: 8 }}>
            <Stack maxW="1180px" mx="auto" gap={6}>
                <Box>
                    <Heading size="lg">Custom Metrics Admin</Heading>
                    <Text color={muted} mt={2}>
                        Scan SEC filings for company KPIs, review pending candidates, and approve the metrics
                        DCF Teacher should track going forward.
                    </Text>
                </Box>

                <Box borderWidth="1px" borderColor={borderColor} borderRadius="md" bg={panelBg} p={4}>
                    <Grid templateColumns={{ base: '1fr', md: 'minmax(160px, 220px) 130px auto auto' }} gap={3} alignItems="end">
                        <Box>
                            <Text fontSize="sm" fontWeight="semibold" mb={1}>Ticker</Text>
                            <Input
                                value={tickerInput}
                                onChange={(event) => setTickerInput(event.target.value)}
                                placeholder="HOOD"
                            />
                        </Box>
                        <Box>
                            <Text fontSize="sm" fontWeight="semibold" mb={1}>Filings</Text>
                            <NativeSelect.Root>
                                <NativeSelect.Field
                                    value={limit}
                                    onChange={(event) => setLimit(Number(event.target.value))}
                                >
                                    {[2, 4, 6, 8, 10].map((value) => (
                                        <option key={value} value={value}>{value}</option>
                                    ))}
                                </NativeSelect.Field>
                                <NativeSelect.Indicator />
                            </NativeSelect.Root>
                        </Box>
                        <Button onClick={() => startScan(limit)} colorPalette="teal" loading={scanning} disabled={!ticker || scanning}>
                            <FiSearch />
                            Scan filings
                        </Button>
                        <Button onClick={loadSuggestions} variant="outline" loading={loading} disabled={!ticker || loading}>
                            <FiRefreshCw />
                            Refresh queue
                        </Button>
                    </Grid>
                    {scanJob && (
                        <Text color={muted} fontSize="sm" mt={3} role="status">
                            {scanJob.status === 'queued' && (scanJob.error || 'Scan queued. Waiting for the worker…')}
                            {scanJob.status === 'running' && (scanJob.total_filings == null
                                ? 'Finding SEC filings…'
                                : `Processed ${scanJob.completed_filings} of ${scanJob.total_filings} filings.`)}
                            {scanJob.status === 'complete' &&
                                `Last scan found ${scanJob.result?.suggestion_count || 0} candidate(s) and saved ${scanJob.result?.upserted_count || 0} pending suggestion(s).`}
                            {scanJob.status === 'failed' && (scanJob.error || 'Scan failed. Please try again.')}
                            {scanning && ' You can leave this page and check back later.'}
                        </Text>
                    )}
                    {scanError && <Text color="red.500" fontSize="sm" mt={3}>{scanError}</Text>}
                </Box>

                {ticker && (
                    <Box borderWidth="1px" borderColor={borderColor} borderRadius="md" bg={panelBg} p={4}>
                        <Heading size="sm" mb={3}>Existing custom metrics for {ticker}</Heading>
                        {existingMetrics.ticker !== ticker || existingMetrics.status === 'loading' ? (
                            <Flex align="center" gap={2}>
                                <Spinner size="sm" />
                                <Text color={muted} fontSize="sm">Loading existing metrics…</Text>
                            </Flex>
                        ) : existingMetrics.status === 'error' ? (
                            <Text color="red.500" fontSize="sm">
                                Unable to load existing metrics. Use Refresh queue to try again.
                            </Text>
                        ) : existingMetrics.metrics.length ? (
                            <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={3}>
                                {existingMetrics.metrics.map((metric) => {
                                    const latest = metric.values?.[metric.values.length - 1];
                                    return (
                                        <Box key={metric.key} borderWidth="1px" borderColor={borderColor} borderRadius="md" p={3}>
                                            <Text fontWeight="semibold">{metric.display_name}</Text>
                                            {latest && (
                                                <Text fontSize="sm" mt={1}>
                                                    {formatValue(latest.value, latest.unit || metric.unit)}
                                                </Text>
                                            )}
                                            <Text color={muted} fontSize="sm" mt={1}>
                                                {latest ? `As of ${formatDate(latest.period_end)} · ` : ''}
                                                {metric.values?.length || 0} saved period{metric.values?.length === 1 ? '' : 's'}
                                            </Text>
                                        </Box>
                                    );
                                })}
                            </Grid>
                        ) : (
                            <Text color={muted} fontSize="sm">No existing custom metrics for this ticker.</Text>
                        )}
                    </Box>
                )}

                {previewPollingError && <Text color="red.500" role="status">{previewPollingError}</Text>}
                {state.error && (
                    <Alert.Root status="error" borderRadius="md">
                        <Alert.Indicator />
                        <Alert.Title>{state.error}</Alert.Title>
                    </Alert.Root>
                )}

                {loading ? (
                    <Flex justify="center" py={12}>
                        <Spinner size="lg" />
                    </Flex>
                ) : state.suggestions.length ? (
                    <Stack gap={4}>
                        {state.suggestions.map((suggestion) => (
                            <MetricSuggestionCard
                                key={suggestion.suggestion_key}
                                suggestion={suggestion}
                                draft={drafts[suggestion.suggestion_key] || {}}
                                reviewStatus={state.reviewStatusByKey[suggestion.suggestion_key]}
                                previewStatus={getPreview(suggestion)?.history_status === 'loading' ? 'loading' : state.previewStatusByKey[suggestion.suggestion_key]}
                                preview={getPreview(suggestion)}
                                onDraftChange={(field, value) => handleDraftChange(suggestion.suggestion_key, field, value)}
                                onPreview={() => handlePreview(suggestion)}
                                onApprove={() => handleReview(suggestion, 'approve')}
                                onReject={() => handleReview(suggestion, 'reject')}
                            />
                        ))}
                    </Stack>
                ) : (
                    <Box borderWidth="1px" borderColor={borderColor} borderRadius="md" bg={panelBg} p={6}>
                        <Heading size="sm" mb={2}>No pending suggestions</Heading>
                        <Text color={muted}>
                            Scan recent filings for a ticker to populate this queue, or switch to another ticker.
                        </Text>
                    </Box>
                )}
            </Stack>
        </Box>
    );
}
